/**
 * Branch Capacity (Act 1): who is working, the heat map with the SOC lane and the
 * utilization strip, and the sandbox. Tabs follow the product's TabsPage look.
 */
import { Box, Tab, Tabs, Typography } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { useDemoSelector } from "../../store/useDemoStore";
import { HeatMap } from "./HeatMap";
import { PerDiemRoster } from "./PerDiemRoster";
import { RosterGrid } from "./RosterGrid";
import { SocLane } from "./SocLane";
import { UtilizationStrip } from "./UtilizationStrip";

/**
 * **Two tabs.** The Sandbox is cut — Pete, 2026-09-22: it is ugly and does not
 * work properly. Nothing in the RFP, the discovery calls or Colin's email asks
 * for a what-if tool; the read they want by territory and discipline is the heat
 * map, and no act or Reserve line ever opened the tab (script author, discussion
 * turn 80). It is a cut, not a defect.
 *
 * The key is gone with the tab, so `/capacity/sandbox` now falls through to Who
 * is working rather than rendering a panel nobody can navigate to. The overlay's
 * own engine stays where it is: `state.sandbox` is read by the heat map, the
 * roster grid and the territory selectors, and with nothing left to set it those
 * reads are inert — unpicking them the night before the session would risk the
 * two screens the demo actually uses to tidy code that now does nothing.
 */
const TABS = [
  { key: "roster", label: "Who is working" },
  { key: "heatmap", label: "Heat map" },
] as const;

type TabKey = (typeof TABS)[number]["key"];

export function CapacityPage() {
  const { tab } = useParams<{ tab?: string }>();
  const navigate = useNavigate();
  const branch = useDemoSelector((s) => s.config.branch.name);
  const bound = useDemoSelector((s) => s.meta.bound);
  const active: TabKey = TABS.some((t) => t.key === tab) ? (tab as TabKey) : "roster";

  return (
    <Box sx={{ py: 2 }}>
      <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, mb: 1 }}>
        <Typography variant="h5">Branch Capacity</Typography>
        <Typography variant="body2" color="text.secondary">
          {branch}
        </Typography>
      </Box>
      <Tabs value={active} onChange={(_, v: TabKey) => navigate(`/capacity/${v}`)} sx={{ mb: 2 }}>
        {TABS.map((t) => (
          <Tab key={t.key} value={t.key} label={t.label} />
        ))}
      </Tabs>
      {!bound && (
        <Typography variant="body2" color="warning.main" sx={{ mb: 1 }}>
          Loading the branch…
        </Typography>
      )}
      {active === "roster" && (
        <Box>
          <RosterGrid />
          <PerDiemRoster />
        </Box>
      )}
      {active === "heatmap" && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <HeatMap />
          <SocLane />
          <UtilizationStrip />
        </Box>
      )}
    </Box>
  );
}
